import api from './api';
import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import * as actionTypes from './actionTypes';
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';
import * as eventTypes from './eventTypes';
import schemas from 'utils/schemas';

export function loadFacilitySublimits(documentId, siteId) {
  return async (dispatch, getState) => {
    const facilitySublimits = getState().facilities.facilitySublimits[documentId];
    let shouldCallApi = !facilitySublimits || (!facilitySublimits.loaded && !facilitySublimits.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_SUBLIMITS_STARTED });

      const response = await dispatch(sendHttpRequest(api.getFacilitySublimits(documentId, siteId)));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilitySublimits));

      dispatch({
        entitiesCategory: 'facilities',
        type: actionTypes.LOAD_FACILITY_SUBLIMITS_SUCCEEDED,
        key: documentId,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_SUBLIMITS_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function saveFacilitySublimit(item, documentId, siteId, callback = () => {}) {
  return async dispatch => {
    dispatch({
      type: actionTypes.SAVE_FACILITY_SUBLIMIT_STARTED
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(api.saveFacilitySublimit(item, documentId, siteId), 'SaveFacilitySublimits', [
        eventTypes.FACILITY_SUBLIMIT_SAVED
      ])
    )
      .then(event => {
        if (event.ok) {
          dispatch({
            type: actionTypes.SAVE_FACILITY_SUBLIMIT_SUCCEEDED,
            payload: { item }
          });
          callback(event.eventBody.sublimitId);
        } else {
          dispatch({
            type: actionTypes.SAVE_FACILITY_SUBLIMIT_FAILED,
            payload: { item: item, error: event }
          });
        }
      })
      .catch(error => {
        dispatch(showError(error.message, error.correlationId));
        dispatch({
          type: actionTypes.SAVE_FACILITY_SUBLIMIT_FAILED,
          payload: { item: item, error }
        });
        callback();
      });
  };
}

export function deleteFacilitySublimit(sublimitId, documentId, siteId, callback = () => {}) {
  return async dispatch => {
    dispatch({
      type: actionTypes.DELETE_FACILITY_SUBLIMIT_STARTED
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(api.deleteFacilitySublimit(sublimitId, documentId, siteId), 'DeleteFacilitySublimit', [
        eventTypes.FACILITY_SUBLIMIT_DELETED
      ])
    )
      .then(event => {
        if (event.ok) {
          dispatch({
            type: actionTypes.DELETE_FACILITY_SUBLIMIT_SUCCEEDED,
            payload: { sublimitId }
          });
          callback();
        } else {
          dispatch({
            type: actionTypes.DELETE_FACILITY_SUBLIMIT_FAILED,
            payload: { sublimitId, error: event }
          });
        }
      })
      .catch(error => {
        dispatch(showError(error.message, error.correlationId));
        dispatch({
          type: actionTypes.DELETE_FACILITY_SUBLIMIT_FAILED,
          payload: { sublimitId, error }
        });
        callback();
      });
  };
}

export function loadFacilitySublimitFilters(sublimitId, siteId) {
  return async (dispatch, getState) => {
    const facilitySublimitFilters = getState().facilities.facilitySublimitFilters[sublimitId];
    let shouldCallApi = !facilitySublimitFilters || (!facilitySublimitFilters.loaded && !facilitySublimitFilters.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_SUBLIMIT_FILTERS_STARTED });

      const response = await dispatch(sendHttpRequest(api.getFacilitySublimitFilters(sublimitId, siteId)));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilitySublimitFilters));

      dispatch({
        entitiesCategory: 'facilities',
        type: actionTypes.LOAD_FACILITY_SUBLIMIT_FILTERS_SUCCEEDED,
        key: sublimitId,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_SUBLIMIT_FILTERS_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function updateFacilitySublimitFilters(data, documentId, siteId, callback) {
  return async dispatch => {
    dispatch({
      type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FILTERS_STARTED
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.updateFacilitySublimitFilters(data.sublimitId, data.filters, data.condition, documentId, siteId),
        'FacilitySublimitFiltersUpdated',
        [eventTypes.FACILITY_SUBLIMIT_FILTERS_UPDATED],
        true
      )
    )
      .then(event => {
        if (event.ok) {
          const { sublimitId, condition, filters } = event.eventBody;
          dispatch({
            type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FILTERS_SUCCEEDED,
            sublimitId,
            condition,
            filters
          });
          callback(condition, filters);
        } else {
          dispatch({
            type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FILTERS_FAILED
          });
        }
      })
      .catch(error => {
        dispatch(showError(error.message, error.correlationId));
        dispatch({
          type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FILTERS_FAILED
        });
      });
  };
}

export function loadFacilitySublimitFinancingStructureFilters(sublimitId, siteId) {
  return async (dispatch, getState) => {
    const facilitySublimitFinancingStructureFilters = getState().facilities.facilitySublimitFinancingStructureFilters[sublimitId];
    let shouldCallApi =
      !facilitySublimitFinancingStructureFilters ||
      (!facilitySublimitFinancingStructureFilters.loaded && !facilitySublimitFinancingStructureFilters.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_STARTED });

      const response = await dispatch(sendHttpRequest(api.getFacilitySublimitFinancingStructureFilters(sublimitId, siteId)));
      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.facilitySublimitFinancingStructureFilters));

      dispatch({
        entitiesCategory: 'facilities',
        type: actionTypes.LOAD_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_SUCCEEDED,
        key: sublimitId,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function updateFacilitySublimitFinancingStructureFilters(data, documentId, siteId, callback) {
  return async dispatch => {
    dispatch({
      type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_STARTED
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.updateFacilitySublimitFinancingStructureFilters(data.sublimitId, data.filters, data.condition, documentId, siteId),
        'FacilitySublimitFinancingStructureFiltersUpdated',
        [eventTypes.FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_UPDATED],
        true
      )
    )
      .then(event => {
        if (event.ok) {
          const { sublimitId, condition, filters } = event.eventBody;
          dispatch({
            type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_SUCCEEDED,
            sublimitId,
            condition,
            filters
          });
          callback(condition, filters);
        } else {
          dispatch({
            type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_FAILED
          });
        }
      })
      .catch(error => {
        dispatch(showError(error.message, error.correlationId));
        dispatch({
          type: actionTypes.UPDATE_FACILITY_SUBLIMIT_FINANCING_STRUCTURE_FILTERS_FAILED
        });
      });
  };
}
